/**
 * Production-ready logging service
 * 
 * Features:
 * - Environment-based log levels
 * - Structured logging with context
 * - Performance monitoring
 * - Error tracking
 * - Component-specific loggers
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

export interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  timestamp?: Date;
  metadata?: Record<string, unknown>;
}

export interface LogEntry extends LogContext {
  level: LogLevel;
  message: string;
  data?: unknown;
}

export interface Logger {
  debug: (message: string, context?: LogContext, data?: unknown) => void;
  info: (message: string, context?: LogContext, data?: unknown) => void;
  warn: (message: string, context?: LogContext, data?: unknown) => void;
  error: (message: string, context?: LogContext, data?: unknown) => void;
  performance: (action: string, duration?: number) => void;
}

class LoggerService {
  private level: LogLevel;
  private isDevelopment: boolean;
  private isProduction: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
    
    // Set log level based on environment
    if (this.isProduction) {
      this.level = (process.env.LOG_LEVEL as LogLevel) || 'error';
    } else {
      this.level = (process.env.LOG_LEVEL as LogLevel) || 'debug';
    }
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.level === 'silent') return false;
    
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1, 
      warn: 2,
      error: 3,
      silent: 4
    };

    return levels[level] >= levels[this.level];
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const component = context?.component ? `[${context.component}]` : '';
    const action = context?.action ? `(${context.action})` : '';
    
    return `${timestamp} ${level.toUpperCase()} ${component}${action} ${message}`;
  }

  private log(level: LogLevel, message: string, context?: LogContext, data?: unknown): void {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(level, message, context);
    const logEntry: LogEntry = {
      level,
      message: formattedMessage,
      ...context,
      timestamp: new Date(),
      data
    };

    // Console output in development
    if (this.isDevelopment) {
      switch (level) {
        case 'debug':
        case 'info':
          console.log(formattedMessage, data || '');
          break;
        case 'warn':
          console.warn(formattedMessage, data || '');
          break;
        case 'error':
          console.error(formattedMessage, data || '');
          break;
      }
    }

    // Production logging could be sent to external service
    if (this.isProduction && level === 'error') {
      this.sendToExternalLogger(logEntry);
    }
  }

  private sendToExternalLogger(entry: LogEntry): void {
    // In production, send to external logging service (Sentry, LogRocket, etc.)
    // For now, just preserve error logs in console for critical issues
    if (entry.level === 'error') {
      console.error('[PROD ERROR]', entry.message, entry.data);
    }
  }

  debug(message: string, context?: LogContext, data?: unknown): void {
    this.log('debug', message, context, data);
  }

  info(message: string, context?: LogContext, data?: unknown): void {
    this.log('info', message, context, data);
  }

  warn(message: string, context?: LogContext, data?: unknown): void {
    this.log('warn', message, context, data);
  }

  error(message: string, context?: LogContext, data?: unknown): void {
    this.log('error', message, context, data);
  }

  performance(component: string, action: string, duration?: number): void {
    if (!this.shouldLog('info')) return;

    const message = duration !== undefined 
      ? `Performance: ${action} completed in ${duration}ms`
      : `Performance: ${action} started`;

    this.log('info', message, { component, action });
  }

  // Component-specific logger factory
  createComponentLogger(component: string): Logger {
    return {
      debug: (message: string, context?: LogContext, data?: unknown) =>
        this.debug(message, { ...context, component }, data),
      info: (message: string, context?: LogContext, data?: unknown) =>
        this.info(message, { ...context, component }, data),
      warn: (message: string, context?: LogContext, data?: unknown) =>
        this.warn(message, { ...context, component }, data),
      error: (message: string, context?: LogContext, data?: unknown) =>
        this.error(message, { ...context, component }, data),
      performance: (action: string, duration?: number) =>
        this.performance(component, action, duration)
    };
  }
}

// Global logger instance
export const logger = new LoggerService();

// Component-specific loggers
export const apiLogger = logger.createComponentLogger('API');
export const editorLogger = logger.createComponentLogger('Editor');
export const authLogger = logger.createComponentLogger('Auth');
export const uploadLogger = logger.createComponentLogger('Upload');
export const imageLogger = logger.createComponentLogger('Image');
export const migrationLogger = logger.createComponentLogger('Migration');
export const flipbookLogger = logger.createComponentLogger('FlipBook');

// Performance measurement utility
export function measurePerformance<T>(
  component: string,
  action: string,
  fn: () => T
): T {
  const start = performance.now();
  const result = fn();
  const duration = performance.now() - start;
  logger.performance(component, action, duration);
  return result;
}

// Async performance measurement
export async function measureAsyncPerformance<T>(
  component: string,
  action: string,
  fn: () => Promise<T>
): Promise<T> {
  const start = performance.now();
  const result = await fn();
  const duration = performance.now() - start;
  logger.performance(component, action, duration);
  return result;
}